import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { toast } from "sonner";
import { Sparkles, Lock, ShieldCheck } from "lucide-react";

export default function Login() {
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, pwd);
      toast.success(`Selamat datang, ${u.name}`);
      if (u.role === "pending") nav("/", { replace: true });
      else nav("/dashboard", { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Email atau kata sandi salah";
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen app-bg flex items-center justify-center px-4 py-12" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-10">
          <img src="/brand/dwos-logo-dark.png" alt="DWOS — Dessy Wedding OS"
            className="w-[180px] h-auto object-contain select-none" draggable={false}/>
        </div>

        <Card className="p-6 md:p-8 border border-[#E5E0D8] bg-white shadow-none">
          <div className="flex items-center gap-2 mb-2">
            <Lock className="w-4 h-4 text-brand"/>
            <h2 className="font-display text-lg font-semibold">Welcome back</h2>
          </div>
          <p className="text-xs text-[#5C6066]">Manage your wedding operations from one place.</p>

          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email">Email Kantor</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email"
                required data-testid="login-email" className="mt-1.5 h-11 bg-white" placeholder="nama@dwg.com" />
            </div>
            <div>
              <Label htmlFor="pwd">Kata Sandi</Label>
              <Input id="pwd" type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} autoComplete="current-password"
                required data-testid="login-password" className="mt-1.5 h-11 bg-white" placeholder="••••••••" />
            </div>
            <Button type="submit" disabled={busy} data-testid="login-submit" className="w-full h-11 bg-brand text-white">
              {busy ? "Memproses..." : "Masuk"}
            </Button>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-[#E5E0D8]"></div></div>
              <div className="relative flex justify-center text-xs"><span className="bg-white px-3 text-[#8C9096]">atau</span></div>
            </div>
            <button type="button" onClick={() => {
              // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
              const redirectUrl = window.location.origin + "/dashboard";
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }} data-testid="btn-google-login"
              className="mt-4 w-full h-11 rounded-md bg-white border border-[#E5E0D8] hover:border-brand transition-colors flex items-center justify-center gap-2 text-sm font-medium">
              <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09 0-.73.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
              Masuk dengan Google
            </button>
          </div>

          <div className="mt-6 pt-4 border-t border-[#F0EBE0] flex items-start gap-2 text-[11px] text-[#8C9096]">
            <ShieldCheck className="w-3.5 h-3.5 mt-0.5 shrink-0 text-emerald-700"/>
            <div>Setelah 5 kali gagal, akun dikunci sementara selama 15 menit. Hanya karyawan resmi dengan akun aktif yang bisa masuk.</div>
          </div>

          <div className="mt-4 text-center">
            <Link to="/register" className="text-xs text-brand hover:underline" data-testid="link-register">Registrasi karyawan baru →</Link>
          </div>
        </Card>

        <div className="mt-6 text-center space-y-1">
          <Link to="/portal" className="text-xs text-[#8C9096] hover:text-brand" data-testid="link-portal">Anda klien? Masuk ke Portal Klien →</Link>
          <div className="text-xs"><Link to="/" className="text-[#8C9096] hover:text-brand">← Kembali ke halaman utama</Link></div>
        </div>
      </div>
    </div>
  );
}
